import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { TaskCounterService, ActiveTask } from '../../../../shared/task-counter.service';
import { getTaskUrl } from '../../../../shared/task-navigation.util';

/** Icon badge in the app bar showing the number of active background tasks, with a dropdown menu listing each task's progress. */
@Component({
  selector: 'app-task-counter-badge',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
  ],
  template: `
    @if ((activeTaskCount$ | async)! > 0) {
      <button
        mat-icon-button
        [matMenuTriggerFor]="taskMenu"
        [matTooltip]="getTooltipText()"
        matTooltipPosition="below"
        class="task-counter-button"
      >
        <mat-icon
          [matBadge]="activeTaskCount$ | async"
          matBadgeColor="accent"
          matBadgeSize="small"
          matBadgePosition="above after"
        >
          work
        </mat-icon>
      </button>
    }

    <mat-menu #taskMenu="matMenu" class="task-menu">
      <div class="task-menu-header">
        <mat-icon>work</mat-icon>
        <span>Active Tasks</span>
      </div>
      <mat-divider></mat-divider>

      @if (activeTasks$ | async; as tasks) {
        <div>
          @for (task of getActiveTasksOnly(tasks); track task) {
            <button mat-menu-item class="task-menu-item" (click)="navigateToTask(task)">
              <div class="task-item-content">
                <div class="task-title">{{ getTaskDisplayName(task) }}</div>
                <div class="task-progress">{{ task.progress }}% complete</div>
              </div>
            </button>
          }
          <mat-divider></mat-divider>
          <button mat-menu-item (click)="navigateToTasksPage()" class="view-details-button">
            <mat-icon>visibility</mat-icon>
            <span>View Details</span>
          </button>
        </div>
      }
    </mat-menu>
  `,
  styles: [
    `
      .task-counter-button {
        margin-right: 8px;
      }

      ::ng-deep .mat-badge-content {
        background: #ff5722 !important;
        color: white !important;
        font-weight: 600 !important;
        font-size: 11px !important;
        min-width: 18px !important;
        height: 18px !important;
        line-height: 18px !important;
      }

      ::ng-deep .task-menu {
        min-width: 280px;
        max-width: 350px;
      }

      .task-menu-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        background: rgba(0, 0, 0, 0.04);
      }

      .task-menu-item {
        padding: 8px 16px !important;
        height: auto !important;
        min-height: 48px !important;
      }

      .task-item-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        width: 100%;
      }

      .task-title {
        font-weight: 500;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.87);
        line-height: 1.2;
      }

      .task-progress {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        line-height: 1.2;
      }

      .view-details-button {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
      }
    `,
  ],
})
export class TaskCounterBadgeComponent implements OnInit, OnDestroy {
  private taskCounterService = inject(TaskCounterService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  activeTaskCount$: Observable<number>;
  activeTasks$: Observable<ActiveTask[]>;

  constructor() {
    this.activeTaskCount$ = this.taskCounterService.activeTaskCount$;
    this.activeTasks$ = this.taskCounterService.activeTasks$;
  }

  ngOnInit(): void {
    ClientLoggingService.log('INFO', '[TaskCounterBadge] Initialized');

    // Debug: Log when active task count changes
    this.activeTaskCount$.subscribe(count => {
      ClientLoggingService.log('INFO', '[TaskCounterBadge] Active task count changed:', count);
    });

    // Debug: Log all tasks
    this.taskCounterService.activeTasks$.subscribe(tasks => {
      ClientLoggingService.log('INFO', '[TaskCounterBadge] All tasks:', tasks);
    });
  }

  ngOnDestroy(): void {
    ClientLoggingService.log('INFO', '[TaskCounterBadge] Destroyed');
  }

  navigateToTask(task: ActiveTask): void {
    this.router.navigate(getTaskUrl(task.projectId, task.taskId));
  }

  navigateToTasksPage(): void {
    const match = this.router.url.match(/\/projects\/([^/]+)/);
    if (match) {
      this.router.navigate(getTaskUrl(match[1]));
    }
  }

  getTooltipText(): string {
    return 'View active tasks';
  }

  getActiveTasksOnly(tasks: ActiveTask[]): ActiveTask[] {
    return tasks.filter(task => task.status === 'pending' || task.status === 'processing');
  }

  getTaskDisplayName(task: ActiveTask): string {
    // Determine task type from metadata, filename, or message
    const taskType = task.metadata?.['taskType'] || this.inferTaskTypeFromTask(task);

    // Extract page number and create a succinct display name
    if (task.metadata && task.metadata['pageNumber']) {
      if (taskType === 'compliance-report') {
        return `Page ${task.metadata['pageNumber']} Compliance Report`;
      } else if (taskType === 'code-applicability') {
        return `Page ${task.metadata['pageNumber']} Code Analysis`;
      } else {
        return `Page ${task.metadata['pageNumber']} Ingestion`;
      }
    }

    // Fallback to filename-based display
    if (task.filename) {
      // Try to extract page info from filename or message
      const pageMatch = task.message?.match(/page (\d+)/i) || task.filename.match(/page[_\s-]?(\d+)/i);
      if (pageMatch) {
        if (taskType === 'compliance-report') {
          return `Page ${pageMatch[1]} Compliance Report`;
        } else if (taskType === 'code-applicability') {
          return `Page ${pageMatch[1]} Code Analysis`;
        } else {
          return `Page ${pageMatch[1]} Ingestion`;
        }
      }

      // Fallback to filename
      const shortFilename = task.filename.length > 20 ? task.filename.substring(0, 17) + '...' : task.filename;
      return `${shortFilename} Processing`;
    }

    return 'Task Processing';
  }

  private inferTaskTypeFromTask(task: ActiveTask): string {
    // Check filename for task type hints
    const filename = task.filename || '';
    if (filename.toLowerCase().includes('compliance report')) {
      return 'compliance-report';
    }
    if (filename.toLowerCase().includes('code applicability') || filename.toLowerCase().includes('analysis')) {
      return 'code-applicability';
    }

    // Check message for task type hints - particularly useful for compliance reports
    const message = task.message || '';
    if (
      message.toLowerCase().includes('compliance report') ||
      message.toLowerCase().includes('compliance analysis') ||
      message.toLowerCase().includes('initialize compliance') ||
      message.toLowerCase().includes('analyze building code') ||
      message.toLowerCase().includes('evaluate compliance') ||
      message.toLowerCase().includes('generate detailed compliance') ||
      message.toLowerCase().includes('save compliance report')
    ) {
      return 'compliance-report';
    }
    if (message.toLowerCase().includes('applicability') || message.toLowerCase().includes('code analysis')) {
      return 'code-applicability';
    }

    return 'plan-ingestion'; // Default
  }
}
